import React, { useState, useEffect, useCallback } from 'react';
import { bookingAPI, userAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { FiRefreshCw, FiPackage, FiSettings, FiX, FiUser, FiStar } from 'react-icons/fi';
import resolveImage from '../../utils/imageUrl';
import AdminServices from './AdminServices';
import './Dashboard.css';

const STATUS_FILTERS = ['all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled'];

// ── Assign Modal ─────────────────────────────────────────
const AssignModal = ({ booking, onClose, onAssigned }) => {
  const [technicians, setTechnicians] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [assigning,   setAssigning]   = useState(null);

  useEffect(() => {
    userAPI.getAvailableTechnicians(booking.city || 'kolkata')
      .then(({ data }) => setTechnicians(data.data || []))
      .catch(() => toast.error('Failed to load technicians'))
      .finally(() => setLoading(false));
  }, [booking.city]);

  const handleAssign = async (technicianId, technicianName) => {
    setAssigning(technicianId);
    try {
      await bookingAPI.assign(booking._id, technicianId);
      toast.success(`Assigned to ${technicianName}`);
      onAssigned();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Assignment failed');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Assign Technician</h3>
            <p className="modal-subtitle">
              {booking.serviceSnapshot?.name} &nbsp;·&nbsp; #{booking.bookingNumber}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}><FiX size={18} /></button>
        </div>

        <div className="modal-body">
          {loading ? (
            <p className="modal-loading">Loading technicians...</p>
          ) : technicians.length === 0 ? (
            <div className="modal-empty">
              <span>👷</span>
              <p>No verified technicians found for this city.</p>
            </div>
          ) : (
            <div className="tech-list">
              {technicians.map((tech) => (
                <div key={tech._id} className="tech-row">
                  <div className="tech-avatar">
                    {tech.profileImage
                      ? <img src={resolveImage(tech.profileImage)} alt={tech.name} />
                      : <span>{tech.name?.[0]}</span>}
                  </div>
                  <div className="tech-info">
                    <p className="tech-name">{tech.name}</p>
                    <p className="tech-meta">
                      <FiStar size={11} /> {tech.rating?.toFixed(1) || '—'}
                      &nbsp;·&nbsp; {tech.totalJobs} jobs
                      &nbsp;·&nbsp;
                      <span style={{ color: tech.isAvailable ? 'var(--status-success)' : 'var(--status-warning)' }}>
                        {tech.isAvailable ? '🟢 Online' : '🔴 Offline'}
                      </span>
                    </p>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={assigning === tech._id}
                    onClick={() => handleAssign(tech._id, tech.name)}
                  >
                    {assigning === tech._id ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Admin Dashboard ──────────────────────────────────────
const AdminDashboard = () => {
  const [activeTab,    setActiveTab]    = useState('bookings');
  const [activeStatus, setActiveStatus] = useState('pending');
  const [bookings,     setBookings]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [stats,        setStats]        = useState({ total: 0, pending: 0, completed: 0 });
  const [assignTarget, setAssignTarget] = useState(null); // booking to assign

  // Stats always come from ALL bookings regardless of active tab filter
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await bookingAPI.getAll({ limit: 1000 });
      const all = data.data || [];
      setStats({
        total:     data.meta?.total ?? all.length,
        pending:   all.filter(b => b.status === 'pending').length,
        completed: all.filter(b => b.status === 'completed').length,
      });
    } catch { /* silent */ }
  }, []);

  const fetchBookings = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = status !== 'all' ? { status } : {};
      const { data } = await bookingAPI.getAll(params);
      setBookings(data.data || []);
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'bookings') fetchBookings(activeStatus);
  }, [activeTab, activeStatus, fetchBookings]);

  const filtered = search
    ? bookings.filter(b =>
        b.bookingNumber?.toLowerCase().includes(search.toLowerCase()) ||
        b.customerId?.name?.toLowerCase().includes(search.toLowerCase()) ||
        b.serviceSnapshot?.name?.toLowerCase().includes(search.toLowerCase())
      )
    : bookings;

  return (
    <div className="dashboard-page">
      <div className="container">

        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h2>Admin Dashboard</h2>
            <p style={{ color: 'var(--brand-muted)', fontSize: '0.85rem', margin: 0 }}>
              Manage bookings and services
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <button
              className={`btn btn-sm ${activeTab === 'bookings' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab('bookings')}
            >
              <FiPackage size={14} /> Bookings
            </button>
            <button
              className={`btn btn-sm ${activeTab === 'services' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab('services')}
            >
              <FiSettings size={14} /> Services
            </button>
          </div>
        </div>

        {/* Bookings tab */}
        {activeTab === 'bookings' && (
          <>
            {/* Stats */}
            <div className="dashboard-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 'var(--sp-6)' }}>
              {[
                { label: 'Total',     value: stats.total,     icon: '📋' },
                { label: 'Pending',   value: stats.pending,   icon: '⏳' },
                { label: 'Completed', value: stats.completed, icon: '✅' },
              ].map(({ label, value, icon }) => (
                <div key={label} className="stat-card">
                  <span className="stat-icon">{icon}</span>
                  <div>
                    <span className="stat-value">{value}</span>
                    <span className="stat-label">{label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Table */}
            <div className="dashboard-section">
              <div className="admin-table-controls">
                <div className="status-tabs" style={{ marginBottom: 0, flex: 1, flexWrap: 'wrap' }}>
                  {STATUS_FILTERS.map(s => (
                    <button key={s}
                      className={`status-tab ${activeStatus === s ? 'active' : ''}`}
                      onClick={() => setActiveStatus(s)}
                    >
                      {s === 'all' ? 'All' : s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => fetchBookings(activeStatus)}>
                  <FiRefreshCw size={13} />
                </button>
                <input
                  className="admin-search-input"
                  type="text"
                  placeholder="Search booking, customer, service..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ minWidth: 200 }}
                />
              </div>

              {loading ? (
                <p style={{ padding: 'var(--sp-8)', textAlign: 'center', color: 'var(--brand-muted)' }}>Loading...</p>
              ) : filtered.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📋</span>
                  <h4>No bookings found</h4>
                </div>
              ) : (
                <div className="jobs-table-wrap">
                  <table className="jobs-table">
                    <thead>
                      <tr>
                        <th>Booking #</th>
                        <th>Service</th>
                        <th>Customer</th>
                        <th>Date & Time</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Expert</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((b) => (
                        <tr key={b._id}>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--brand-muted)' }}>
                            {b.bookingNumber}
                          </td>
                          <td className="td-service">{b.serviceSnapshot?.name}</td>
                          <td className="td-customer">
                            <div>{b.customerId?.name}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--brand-muted)' }}>{b.customerId?.phone}</div>
                          </td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                            {b.scheduledDate && format(new Date(b.scheduledDate), 'dd MMM yy')}
                            <br />
                            <span style={{ color: 'var(--brand-muted)', fontSize: '0.72rem' }}>
                              {b.scheduledTimeSlot}
                            </span>
                          </td>
                          <td><strong>₹{b.totalAmount}</strong></td>
                          <td><span className={`badge badge-${b.status}`}>{b.status.replace('_', ' ')}</span></td>
                          <td style={{ fontSize: '0.8rem' }}>
                            {b.technicianId?.name
                              ? <span style={{ color: 'var(--brand-ink)' }}><FiUser size={11} /> {b.technicianId.name}</span>
                              : <span style={{ color: 'var(--status-warning)' }}>Unassigned</span>}
                          </td>
                          <td>
                            {['pending', 'confirmed'].includes(b.status) && (
                              <button
                                className="btn btn-sm btn-primary"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '4px 10px' }}
                                onClick={() => setAssignTarget(b)}
                              >
                                {b.technicianId ? 'Reassign' : 'Assign'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Services tab */}
        {activeTab === 'services' && (
          <div className="dashboard-section">
            <AdminServices />
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {assignTarget && (
        <AssignModal
          booking={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { fetchBookings(activeStatus); fetchStats(); }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
